import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../utils/logger';

interface VendorAddressSnapshot {
  vendorId: string;
  street_line1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  lastUpdated: number;
}

interface VendorAddressRegistry {
  [vendorId: string]: VendorAddressSnapshot;
}

/**
 * Service to track vendor address changes and invalidate delivery serviceability cache when addresses change
 */
class VendorAddressTracker {
  private addressRegistry: VendorAddressRegistry = {};
  private readonly STORAGE_KEY = 'vendor_address_registry';
  private readonly COORDINATE_PRECISION = 0.001; // ~100 meters tolerance for coordinate changes

  constructor() {
    this.loadRegistry();
  }

  /**
   * Load vendor address registry from AsyncStorage
   */
  private async loadRegistry() {
    try {
      const stored = await AsyncStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.addressRegistry = JSON.parse(stored);
        logger.debug('AddressTracker', `Loaded address registry with ${Object.keys(this.addressRegistry).length} vendors`);
      }
    } catch (error) {
      logger.warn('AddressTracker', 'Failed to load address registry', error);
    }
  }

  /**
   * Save vendor address registry to AsyncStorage
   */
  private async saveRegistry() {
    try {
      await AsyncStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.addressRegistry));
    } catch (error) {
      logger.warn('AddressTracker', 'Failed to save address registry', error);
    }
  }

  /**
   * Create address snapshot from vendor data
   */
  private createAddressSnapshot(vendor: any): VendorAddressSnapshot {
    return {
      vendorId: vendor.id,
      street_line1: vendor.street_line1,
      city: vendor.city,
      state: vendor.state,
      zip_code: vendor.zip_code,
      country: vendor.country,
      latitude: vendor.latitude,
      longitude: vendor.longitude,
      lastUpdated: Date.now()
    };
  }

  /**
   * Compare two address snapshots to detect changes
   */
  private hasAddressChanged(previous: VendorAddressSnapshot, current: VendorAddressSnapshot): boolean {
    // Check basic address fields
    if (
      previous.street_line1 !== current.street_line1 ||
      previous.city !== current.city ||
      previous.state !== current.state ||
      previous.zip_code !== current.zip_code ||
      previous.country !== current.country
    ) {
      return true;
    }

    // Check coordinate changes (with tolerance for small GPS variations)
    const latChanged = previous.latitude !== undefined && current.latitude !== undefined &&
      Math.abs(previous.latitude - current.latitude) > this.COORDINATE_PRECISION;
    
    const lngChanged = previous.longitude !== undefined && current.longitude !== undefined &&
      Math.abs(previous.longitude - current.longitude) > this.COORDINATE_PRECISION;

    if (latChanged || lngChanged) {
      return true;
    }

    // Check if coordinates were added or removed
    const coordsAdded = (previous.latitude === undefined || previous.longitude === undefined) &&
      (current.latitude !== undefined && current.longitude !== undefined);
    
    const coordsRemoved = (previous.latitude !== undefined && previous.longitude !== undefined) &&
      (current.latitude === undefined || current.longitude === undefined);

    return coordsAdded || coordsRemoved;
  }

  /**
   * Process products and detect vendors with address changes
   * Returns array of vendor IDs that need cache invalidation
   */
  async processProducts(products: any[]): Promise<string[]> {
    const vendorsWithChanges: string[] = [];
    const currentTime = Date.now();

    for (const product of products) {
      const vendor = product.vendor;
      if (!vendor?.id) continue;

      const currentSnapshot = this.createAddressSnapshot(vendor);
      const previousSnapshot = this.addressRegistry[vendor.id];

      if (previousSnapshot) {
        // Check if address has changed
        if (this.hasAddressChanged(previousSnapshot, currentSnapshot)) {
          logger.info('AddressTracker', `Address change detected for vendor ${vendor.store_name} (${vendor.id})`, {
            previous: {
              street: previousSnapshot.street_line1,
              city: previousSnapshot.city,
              coords: `${previousSnapshot.latitude},${previousSnapshot.longitude}`
            },
            current: {
              street: currentSnapshot.street_line1,
              city: currentSnapshot.city,
              coords: `${currentSnapshot.latitude},${currentSnapshot.longitude}`
            }
          });
          
          vendorsWithChanges.push(vendor.id);
        }
      } else {
        // First time seeing this vendor
        logger.debug('AddressTracker', `New vendor registered: ${vendor.store_name} (${vendor.id})`);
      }

      // Update registry with current snapshot
      this.addressRegistry[vendor.id] = currentSnapshot;
    }

    // Save updated registry
    if (vendorsWithChanges.length > 0 || Object.keys(this.addressRegistry).length !== Object.keys(this.addressRegistry).length) {
      await this.saveRegistry();
    }

    return vendorsWithChanges;
  }

  /**
   * Get the last known address for a vendor
   */
  getLastKnownAddress(vendorId: string): VendorAddressSnapshot | null {
    return this.addressRegistry[vendorId] || null;
  }

  /**
   * Clear address history for a vendor (useful for testing or cleanup)
   */
  async clearVendorHistory(vendorId: string) {
    delete this.addressRegistry[vendorId];
    await this.saveRegistry();
  }

  /**
   * Clear all address history (useful for testing or reset)
   */
  async clearAllHistory() {
    this.addressRegistry = {};
    await this.saveRegistry();
  }

  /**
   * Get statistics about tracked vendors
   */
  getStats() {
    const totalVendors = Object.keys(this.addressRegistry).length;
    const withCoordinates = Object.values(this.addressRegistry).filter(
      snapshot => snapshot.latitude !== undefined && snapshot.longitude !== undefined
    ).length;

    return {
      totalVendors,
      withCoordinates,
      withoutCoordinates: totalVendors - withCoordinates
    };
  }

  /**
   * Force an address change for testing purposes
   * This simulates a vendor changing their address
   */
  async simulateAddressChange(vendorId: string, changes: Partial<VendorAddressSnapshot>) {
    const existing = this.addressRegistry[vendorId];
    if (!existing) {
      logger.warn('AddressTracker', `Cannot simulate change for unknown vendor: ${vendorId}`);
      return false;
    }

    // Apply changes
    this.addressRegistry[vendorId] = {
      ...existing,
      ...changes,
      lastUpdated: Date.now()
    };

    await this.saveRegistry();
    logger.info('AddressTracker', `Simulated address change for vendor ${vendorId}`, changes);
    return true;
  }

  /**
   * Get detailed information about all tracked vendors
   */
  getDetailedRegistry(): VendorAddressRegistry {
    return { ...this.addressRegistry };
  }
}

export const vendorAddressTracker = new VendorAddressTracker();